import { useMemo, useState } from 'react';
import {
  Card, Button, Input, Select, Modal, Form, Row, Col, Typography,
  Checkbox, Flex,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { fmtDataBr } from '../../utils/dataFmt';
import type { Transacao } from './types';
import { CLASSIFICACOES, BANCO_LABEL } from './types';
import { fmt } from './utils';

const { Text } = Typography;

// Modal "Classificar transação" — botoes coloridos por classificacao,
// opcional "salvar como regra" pra aplicar em transacoes similares.
export default function ModalClassificar({ transacao, onFechar, onSalvar }: {
  transacao: Transacao; onFechar: () => void;
  onSalvar: (t: Transacao, classif: string, regra?: { contem_texto: string; sinal_valor: string }) => void;
}) {
  const [classifEscolhida, setClassifEscolhida] = useState(transacao.classificacao);
  const sugestaoTexto = useMemo(() => {
    const palavras = transacao.descricao.split(/[:·\-,]/)[0].trim()
      .split(/\s+/).filter(w => w.length > 3 && !/^(de|do|da|para|pelo|para|que|com|por)$/i.test(w));
    return palavras.slice(0, 3).join(' ');
  }, [transacao.descricao]);
  const [criarRegra, setCriarRegra] = useState(false);
  const [textoRegra, setTextoRegra] = useState(sugestaoTexto);
  const [sinalRegra, setSinalRegra] = useState<'qualquer' | 'positivo' | 'negativo'>(
    transacao.valor > 0 ? 'positivo' : 'negativo');

  function salvar() {
    onSalvar(transacao, classifEscolhida,
      criarRegra ? { contem_texto: textoRegra.trim(), sinal_valor: sinalRegra } : undefined);
  }
  const ehReceita = transacao.valor > 0;
  const podeSalvar = classifEscolhida && classifEscolhida !== transacao.classificacao;

  return (
    <Modal open onCancel={onFechar} onOk={salvar}
      okText={`Salvar${criarRegra ? ' + criar regra' : ''}`}
      okButtonProps={{ disabled: !podeSalvar, icon: <CheckOutlined />, style: podeSalvar ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : undefined }}
      cancelText="Cancelar" width={600} title="Classificar transação"
    >
      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 16 }} bodyStyle={{ padding: 10 }}>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>{fmtDataBr(transacao.data_transacao)}</Text>
        <div><Text style={{ fontSize: 13 }}>{transacao.descricao}</Text></div>
        <Flex justify="space-between" align="baseline">
          <Text type="secondary" style={{ fontSize: 10 }}>
            {BANCO_LABEL[transacao.banco] || transacao.banco} {transacao.conta_id} · {transacao.tipo_ofx}
          </Text>
          <Text style={{ fontSize: 18, fontWeight: 700, color: ehReceita ? '#0a7f4a' : '#B91626' }}>
            {ehReceita ? '+' : ''}{fmt(transacao.valor)}
          </Text>
        </Flex>
      </Card>

      <Form layout="vertical" size="small">
        <Form.Item label={<Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Classificar como</Text>}>
          <Row gutter={[6, 6]}>
            {CLASSIFICACOES.filter(c => c.id !== 'nao_classificada').map(c => (
              <Col key={c.id} xs={12} sm={8}>
                <Button
                  block size="middle"
                  type={classifEscolhida === c.id ? 'primary' : 'default'}
                  style={classifEscolhida === c.id
                    ? { background: c.cor, borderColor: c.cor }
                    : { color: c.cor, borderColor: c.cor, background: c.bg }}
                  onClick={() => setClassifEscolhida(c.id)}
                >
                  {c.emoji} {c.label}
                </Button>
              </Col>
            ))}
          </Row>
        </Form.Item>

        {classifEscolhida !== transacao.classificacao && classifEscolhida !== 'nao_classificada' && (
          <Card size="small" style={{ background: '#FFF8E8', borderStyle: 'dashed', borderColor: '#E0B454' }} bodyStyle={{ padding: 10 }}>
            <Checkbox checked={criarRegra} onChange={e => setCriarRegra(e.target.checked)}>
              <Text strong style={{ color: '#7A5500' }}>🤖 Salvar como regra (aplica em outras transações similares)</Text>
            </Checkbox>
            {criarRegra && (
              <Row gutter={8} style={{ marginTop: 10 }}>
                <Col span={16}>
                  <Form.Item label="Quando a descrição contém *" style={{ marginBottom: 0 }}>
                    <Input value={textoRegra} onChange={e => setTextoRegra(e.target.value)} placeholder="ex: Pix recebido" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Valor" style={{ marginBottom: 0 }}>
                    <Select value={sinalRegra} onChange={(v) => setSinalRegra(v)}
                      options={[
                        { label: 'Qualquer', value: 'qualquer' },
                        { label: 'Crédito (+)', value: 'positivo' },
                        { label: 'Débito (−)', value: 'negativo' },
                      ]} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Card>
        )}
      </Form>
    </Modal>
  );
}
